import React from 'react';
import { FeaturePageLayout } from '@/components/shared/FeaturePageLayout';
import { Users } from 'lucide-react';

export default function LeadCapturePage() {
  return (
    <FeaturePageLayout
      icon={Users}
      title="Lead Capture"
      badge="Growth Feature"
      description="Automatically collect customer details, contact information, and buying preferences during natural WhatsApp conversations. Convert casual inquiries into qualified leads without forms or landing pages."
      capabilities={[
        {
          title: 'Conversational Data Collection',
          description: 'The AI naturally asks for name, phone, email, and preferences during the chat flow — no jarring forms needed.',
        },
        {
          title: 'Custom Lead Fields',
          description: 'Define which information you want to capture: budget, location, product interest, timeline, or any custom field.',
        },
        {
          title: 'Auto Lead Scoring',
          description: 'Leads are automatically scored based on engagement level, response quality, and buying signals detected by AI.',
        },
        {
          title: 'CRM Export',
          description: 'Export captured leads in CSV format or integrate with your existing CRM. Keep all your data organized.',
        },
        {
          title: 'Duplicate Detection',
          description: 'The system detects returning customers and merges their information to avoid duplicate leads.',
        },
        {
          title: 'Real-Time Notifications',
          description: 'Get instant alerts when a high-intent lead is captured. Never miss a potential sale.',
        },
      ]}
      howItWorks={[
        {
          title: 'Define Your Lead Fields',
          description: 'Choose which customer details you want to capture — name, email, phone, location, budget, product interest, etc.',
        },
        {
          title: 'AI Collects During Conversation',
          description: 'As customers chat with your AI agent, it naturally gathers information without disrupting the conversation flow.',
        },
        {
          title: 'Leads Appear in Dashboard',
          description: 'All captured leads are organized in your Agentick dashboard with timestamps, scores, and conversation history.',
        },
        {
          title: 'Export & Follow Up',
          description: 'Export leads to your CRM or follow up directly from WhatsApp. Close the loop faster.',
        },
      ]}
      useCases={[
        { emoji: '🏬', title: 'E-commerce', description: 'Capture product interest and contact details from browsers who message on WhatsApp before buying.' },
        { emoji: '🏗️', title: 'Real Estate', description: 'Collect buyer preferences, budget ranges, and location interests during property inquiry conversations.' },
        { emoji: '📚', title: 'Education', description: 'Gather student details, course preferences, and enrollment intent from admission inquiries.' },
        { emoji: '💊', title: 'Healthcare', description: 'Collect patient information, symptoms, and appointment preferences for follow-up and scheduling.' },
        { emoji: '🔧', title: 'Home Services', description: 'Capture service requests with location, urgency, and budget info from customers seeking help.' },
        { emoji: '📱', title: 'SaaS & Tech', description: 'Qualify inbound interest by collecting company size, use case, and decision-maker details.' },
      ]}
      ctaText="Start capturing leads on WhatsApp today"
    />
  );
}
